import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

import Login          from './pages/Login';
import Dashboard      from './pages/Dashboard';
import Dossiers       from './pages/Dossiers';
import NouveauDossier from './pages/NouveauDossier';
import Wallet         from './pages/Wallet';
import Formalistes    from './pages/Formalistes';
import Marketplace    from './pages/Marketplace';
import Config         from './pages/Config';
import MonCompte      from './pages/MonCompte';
import Formaliste     from './pages/Formaliste';
import Admin          from './pages/Admin';
import Catalogue      from './pages/Catalogue';
import Imports        from './pages/Imports';
import Pilotage       from './pages/Pilotage';
import Facturation    from './pages/Facturation';
import Qualite        from './pages/Qualite';
import { Previsions, Benchmark, Rapports } from './pages/PilotagePages';
import Developpeurs from './pages/Developpeurs';
import Notifications  from './pages/Notifications';
import Activite       from './pages/Activite';
import PlanGate from './components/PlanGate';

// Nouvelles pages
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import JAL            from './pages/JAL';
import Aircall        from './pages/Aircall';
import Zendesk        from './pages/Zendesk';
import SuiviINPI      from './pages/SuiviINPI';
import Lexa           from './pages/Lexa';
import Signatures     from './pages/Signatures';
import Branding       from './pages/Branding';
import SuiviEmails    from './pages/SuiviEmails';

import './index.css';

const W = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

function AppRoutes() {
  const { impersonating, user, stopImpersonation } = useAuth();

  const handleStop = () => {
    stopImpersonation();
    window.location.href = '/admin';
  };

  return (
    <>
      {impersonating && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:9999,
          background:'#F0A843', color:'#09090E',
          display:'flex', alignItems:'center', justifyContent:'center',
          gap:'12px', padding:'8px 16px',
          fontFamily:"'DM Sans', sans-serif", fontSize:'13px', fontWeight:500,
          boxShadow:'0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <span>Connecté en tant que {user?.prenom} {user?.nom}</span>
          <button onClick={handleStop} style={{
            background:'#09090E', color:'#F0A843', border:'none',
            padding:'4px 12px', borderRadius:'5px', fontSize:'12px',
            fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif",
          }}>
            Quitter
          </button>
        </div>
      )}
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/login"      element={<Login />} />
        <Route path="/"           element={<Navigate to="/dashboard" replace />} />
        <Route path="/formaliste" element={<ProtectedRoute roles={['admin','superviseur','formaliste']}><Formaliste /></ProtectedRoute>} />
        <Route path="/admin"      element={<ProtectedRoute roles={['admin']}><Admin /></ProtectedRoute>} />
        <Route path="/admin/imports" element={<ProtectedRoute roles={['admin']}><Imports /></ProtectedRoute>} />
        <Route path="/catalogue" element={<ProtectedRoute roles={['admin']}><Catalogue /></ProtectedRoute>} />

        {/* Dossiers */}
        <Route path="/dashboard"        element={<W><Dashboard /></W>} />
        <Route path="/dossiers"         element={<W><Dossiers /></W>} />
        <Route path="/dossiers/nouveau" element={<W><NouveauDossier /></W>} />
        <Route path="/dossiers/inpi"    element={<W><SuiviINPI /></W>} />
        <Route path="/lexa"             element={<W><Lexa /></W>} />
        <Route path="/notifications"    element={<W><Notifications /></W>} />
        <Route path="/activite"         element={<W><Activite /></W>} />
        <Route path="/wallet"           element={<W><Wallet /></W>} />
        <Route path="/jal"              element={<W><JAL /></W>} />
        <Route path="/signatures"       element={<W><Signatures /></W>} />
        <Route path="/emails"           element={<W><SuiviEmails /></W>} />
        <Route path="/branding"          element={<W><Branding /></W>} />

        {/* Automatisation */}
        <Route path="/marketplace"      element={<W><Marketplace /></W>} />
        <Route path="/connecteurs" element={<Navigate to="/config" replace />} />
        <Route path="/config"           element={<W><Config /></W>} />

        {/* Analyse */}
        <Route path="/pilotage"         element={<W><Pilotage /></W>} />
        <Route path="/qualite"          element={<W><PlanGate minPlan="pro"><Qualite /></PlanGate></W>} />
        <Route path="/rapports"         element={<W><PlanGate minPlan="pro"><Rapports /></PlanGate></W>} />
        <Route path="/previsions"       element={<W><PlanGate minPlan="enterprise"><Previsions /></PlanGate></W>} />
        <Route path="/benchmark"        element={<W><PlanGate minPlan="enterprise"><Benchmark /></PlanGate></W>} />

        {/* Compte */}
        <Route path="/formalistes"      element={<W><Formalistes /></W>} />
        <Route path="/mon-compte"       element={<W><MonCompte /></W>} />
        <Route path="/facturation"      element={<W><Facturation /></W>} />
        <Route path="/zendesk"          element={<W><Zendesk /></W>} />
        <Route path="/aircall"          element={<W><Aircall /></W>} />
        <Route path="/developpeurs"     element={<W><PlanGate minPlan="enterprise"><Developpeurs /></PlanGate></W>} />

        {/* Redirects legacy */}
        <Route path="/agents-actifs"    element={<Navigate to="/marketplace" replace />} />
        <Route path="/agents"           element={<Navigate to="/marketplace" replace />} />
        <Route path="/equipe"           element={<Navigate to="/formalistes" replace />} />
        <Route path="/messagerie"       element={<Navigate to="/dossiers" replace />} />

        <Route path="*"                 element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
